import { useCallback, useRef, useEffect } from 'react';

export interface SoundConfig {
  volume?: number;
  playbackRate?: number;
  loop?: boolean;
}

interface SoundEffectsHook {
  playSound: (soundType: SoundType, config?: SoundConfig) => void;
  stopSound: (soundType: SoundType) => void;
  stopAllSounds: () => void;
  setMasterVolume: (volume: number) => void;
  toggleMute: () => void;
  isMuted: boolean;
}

export type SoundType = 
  // Interface sounds
  | 'buttonHover'
  | 'buttonClick'
  | 'pageTransition'
  | 'notification'
  | 'modalOpen'
  | 'modalClose'
  
  // Gameplay sounds
  | 'categorySelect'
  | 'categoryArts'
  | 'categoryScience'
  | 'categorySports'
  | 'categoryHistory'
  | 'categoryGeography'
  | 'categoryEntertainment'
  | 'categoryMystery'
  | 'questionAppear'
  | 'answerCorrect'
  | 'answerWrong'
  | 'categoryComplete'
  | 'gameWin'
  | 'gameLose'
  | 'streakBuilding'
  | 'mysteryActivated'
  | 'attackSuccess'
  | 'attackFailed'
  
  // Ambient sounds
  | 'lobbyAmbient'
  | 'gameAmbient'
  | 'tensionBuilding'
  | 'countdown';

// Générateur de sons procéduraux utilisant Web Audio API
class SoundGenerator {
  private audioContext: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private activeSounds: Map<SoundType, AudioBufferSourceNode> = new Map();
  private _isMuted: boolean = false;
  private _masterVolume: number = 0.7;

  constructor() {
    this.initAudioContext();
  }

  private initAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      this.masterGain = this.audioContext.createGain();
      this.masterGain.connect(this.audioContext.destination);
      this.masterGain.gain.value = this._masterVolume;
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
    }
  }

  private async resumeAudioContext() {
    if (this.audioContext?.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  // Générateur de tonalités avec enveloppe ADSR
  private createTone(
    frequency: number, 
    duration: number, 
    waveType: OscillatorType = 'sine',
    envelope?: { attack: number; decay: number; sustain: number; release: number }
  ): AudioBuffer | null {
    if (!this.audioContext) return null;

    const sampleRate = this.audioContext.sampleRate;
    const length = Math.floor(sampleRate * duration);
    const buffer = this.audioContext.createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);

    const defaultEnvelope = { attack: 0.1, decay: 0.2, sustain: 0.6, release: 0.3 };
    const env = { ...defaultEnvelope, ...envelope };

    for (let i = 0; i < length; i++) {
      const time = i / sampleRate;
      const progress = i / length;
      
      // Génération de la forme d'onde
      let sample = 0;
      switch (waveType) {
        case 'sine':
          sample = Math.sin(2 * Math.PI * frequency * time);
          break;
        case 'square':
          sample = Math.sign(Math.sin(2 * Math.PI * frequency * time));
          break;
        case 'sawtooth':
          sample = 2 * (frequency * time - Math.floor(frequency * time + 0.5));
          break;
        case 'triangle':
          sample = 2 * Math.abs(2 * (frequency * time - Math.floor(frequency * time + 0.5))) - 1;
          break;
      }

      // Application de l'enveloppe ADSR
      let amplitude = 1;
      if (progress < env.attack) {
        amplitude = progress / env.attack;
      } else if (progress < env.attack + env.decay) {
        const decayProgress = (progress - env.attack) / env.decay;
        amplitude = 1 - decayProgress * (1 - env.sustain);
      } else if (progress < 1 - env.release) {
        amplitude = env.sustain;
      } else {
        const releaseProgress = (progress - (1 - env.release)) / env.release;
        amplitude = env.sustain * (1 - releaseProgress);
      }

      data[i] = sample * amplitude;
    }

    return buffer;
  }

  // Générateur de bruit avec filtrage
  private createNoise(duration: number, filterFreq: number = 1000): AudioBuffer | null {
    if (!this.audioContext) return null;

    const sampleRate = this.audioContext.sampleRate;
    const length = Math.floor(sampleRate * duration);
    const buffer = this.audioContext.createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);

    // Génération de bruit blanc
    for (let i = 0; i < length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (sampleRate * 0.1));
    }

    return buffer;
  }

  // Création de sons complexes en combinant plusieurs tonalités
  private createComplexSound(soundType: SoundType): AudioBuffer | null {
    switch (soundType) {
      case 'buttonHover':
        return this.createTone(800, 0.1, 'sine', { attack: 0.01, decay: 0.05, sustain: 0.7, release: 0.04 });

      case 'buttonClick':
        return this.createTone(600, 0.15, 'square', { attack: 0.01, decay: 0.08, sustain: 0.3, release: 0.06 });

      case 'answerCorrect':
        // Accord majeur ascendant
        return this.createChord([523.25, 659.25, 783.99], 0.8, 'sine'); // C-E-G

      case 'answerWrong':
        return this.createTone(200, 0.5, 'sawtooth', { attack: 0.01, decay: 0.1, sustain: 0.4, release: 0.39 });

      case 'categoryComplete':
        // Fanfare majestueuse
        return this.createChord([523.25, 659.25, 783.99, 1046.5], 1.5, 'triangle');

      case 'gameWin':
        // Mélodie de victoire
        return this.createMelody([
          { freq: 523.25, duration: 0.3 }, // C
          { freq: 659.25, duration: 0.3 }, // E
          { freq: 783.99, duration: 0.3 }, // G
          { freq: 1046.5, duration: 0.6 }  // C
        ]);

      case 'categoryArts':
        return this.createTone(440, 0.3, 'triangle', { attack: 0.1, decay: 0.1, sustain: 0.6, release: 0.1 });

      case 'categoryScience':
        return this.createTone(880, 0.3, 'square', { attack: 0.05, decay: 0.1, sustain: 0.7, release: 0.15 });

      case 'categorySports':
        return this.createTone(330, 0.3, 'sawtooth', { attack: 0.02, decay: 0.1, sustain: 0.8, release: 0.08 });

      case 'categoryMystery':
        return this.createTone(220, 0.5, 'sine', { attack: 0.2, decay: 0.1, sustain: 0.5, release: 0.2 });

      case 'notification':
        return this.createChord([659.25, 783.99], 0.4, 'sine');

      case 'mysteryActivated':
        return this.createTone(110, 0.8, 'triangle', { attack: 0.3, decay: 0.2, sustain: 0.3, release: 0.2 });

      default:
        return this.createTone(440, 0.2, 'sine');
    }
  }

  private createChord(frequencies: number[], duration: number, waveType: OscillatorType): AudioBuffer | null {
    if (!this.audioContext) return null;

    const sampleRate = this.audioContext.sampleRate;
    const length = Math.floor(sampleRate * duration);
    const buffer = this.audioContext.createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < length; i++) {
      const time = i / sampleRate;
      let sample = 0;
      
      frequencies.forEach(freq => {
        sample += Math.sin(2 * Math.PI * freq * time) / frequencies.length;
      });

      // Enveloppe simple
      const progress = i / length;
      let amplitude = 1;
      if (progress > 0.7) {
        amplitude = (1 - progress) / 0.3;
      }

      data[i] = sample * amplitude;
    }

    return buffer;
  }

  private createMelody(notes: { freq: number; duration: number }[]): AudioBuffer | null {
    if (!this.audioContext) return null;

    const totalDuration = notes.reduce((sum, note) => sum + note.duration, 0);
    const sampleRate = this.audioContext.sampleRate;
    const length = Math.floor(sampleRate * totalDuration);
    const buffer = this.audioContext.createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);

    let currentTime = 0;
    
    notes.forEach(note => {
      const startSample = Math.floor(currentTime * sampleRate);
      const noteSamples = Math.floor(note.duration * sampleRate);

      for (let i = 0; i < noteSamples; i++) {
        const time = i / sampleRate;
        const progress = i / noteSamples;
        
        // Enveloppe pour chaque note
        let amplitude = 1;
        if (progress < 0.1) amplitude = progress / 0.1;
        else if (progress > 0.8) amplitude = (1 - progress) / 0.2;

        const sample = Math.sin(2 * Math.PI * note.freq * time) * amplitude;
        
        if (startSample + i < length) {
          data[startSample + i] = sample;
        }
      }

      currentTime += note.duration;
    });

    return buffer;
  }

  async playSound(soundType: SoundType, config: SoundConfig = {}): Promise<void> {
    if (!this.audioContext || !this.masterGain || this._isMuted) return;

    await this.resumeAudioContext();

    // Arrêter le son précédent du même type
    this.stopSound(soundType);

    const buffer = this.createComplexSound(soundType);
    if (!buffer) return;

    const source = this.audioContext.createBufferSource();
    const gainNode = this.audioContext.createGain();
    
    source.buffer = buffer;
    source.playbackRate.value = config.playbackRate || 1;
    source.loop = config.loop || false;
    
    gainNode.gain.value = (config.volume || 1) * this._masterVolume;
    
    source.connect(gainNode);
    gainNode.connect(this.masterGain);
    
    this.activeSounds.set(soundType, source);
    
    source.onended = () => {
      this.activeSounds.delete(soundType);
    };
    
    source.start();
  }

  stopSound(soundType: SoundType): void {
    const sound = this.activeSounds.get(soundType);
    if (sound) {
      sound.stop();
      this.activeSounds.delete(soundType);
    }
  }

  stopAllSounds(): void {
    this.activeSounds.forEach(sound => sound.stop());
    this.activeSounds.clear();
  }

  setMasterVolume(volume: number): void {
    this._masterVolume = Math.max(0, Math.min(1, volume));
    if (this.masterGain) {
      this.masterGain.gain.value = this._isMuted ? 0 : this._masterVolume;
    }
  }

  toggleMute(): boolean {
    this._isMuted = !this._isMuted;
    if (this.masterGain) {
      this.masterGain.gain.value = this._isMuted ? 0 : this._masterVolume;
    }
    return this._isMuted;
  }

  get isMuted(): boolean {
    return this._isMuted;
  }
}

export const useSoundEffects = (): SoundEffectsHook => {
  const soundGeneratorRef = useRef<SoundGenerator | null>(null);

  useEffect(() => {
    if (!soundGeneratorRef.current) {
      soundGeneratorRef.current = new SoundGenerator();
    }

    return () => {
      soundGeneratorRef.current?.stopAllSounds();
    };
  }, []);

  const playSound = useCallback((soundType: SoundType, config?: SoundConfig) => {
    soundGeneratorRef.current?.playSound(soundType, config);
  }, []);

  const stopSound = useCallback((soundType: SoundType) => {
    soundGeneratorRef.current?.stopSound(soundType);
  }, []);

  const stopAllSounds = useCallback(() => {
    soundGeneratorRef.current?.stopAllSounds();
  }, []);

  const setMasterVolume = useCallback((volume: number) => {
    soundGeneratorRef.current?.setMasterVolume(volume);
  }, []);

  const toggleMute = useCallback(() => {
    return soundGeneratorRef.current?.toggleMute() || false;
  }, []);

  return {
    playSound,
    stopSound,
    stopAllSounds,
    setMasterVolume,
    toggleMute,
    isMuted: soundGeneratorRef.current?.isMuted || false
  };
};
